import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { toast } from '@/hooks/use-toast';
import {
  CategoriesSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { useProducerFormData } from '@/hooks/useProducerFormData';
import { useProducerAccess } from '@/hooks/useProducerAccess';

export default function ProducerKategorier() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateProducerMutation = useUpdateProducer();
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);

  const isDemo = farmId === 'demo';
  const canEdit = isDemo || hasCapability('canEditProducerInfo');

  const { initialFormData } = useProducerFormData(
    producer,
    isDemo,
    farmId
  );

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (isDemo) {
        toast({ title: 'Sparat', description: 'Sparat (demo).' });
        return;
      }
      if (!producer) return;

      const payload = {
        categories: data.categories,
        capability_suggestion: data.capabilitySuggestion || null,
      };
      
      await updateProducerMutation.mutateAsync({
        id: producer.id,
        updates: payload,
      });

      toast({ title: 'Sparat', description: 'Dina ändringar har sparats.' });
    },
  });

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Kategorier">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Kategorier">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Kategorier"
    >
      <ProducerSectionHeader
        title="Kategorier"
        description="Välj vilka kategorier som passar din gård."
        onSave={canEdit ? editorState.save : undefined}
        isSaving={editorState.isSaving || updateProducerMutation.isPending}
        isDirty={editorState.isDirty}
        isReadOnly={!canEdit}
      />

      <CategoriesSection
        formData={editorState.formData}
        toggleCategory={editorState.toggleCategory}
        addCategory={editorState.addCategory}
        onCapabilitySuggestionChange={(v) => editorState.updateField('capabilitySuggestion', v)}
      />
    </ProducerLayout>
  );
}